import { useState } from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import { LEGAL_RIGHTS } from '../data/legalRights';
import {
  Scale,
  Shield,
  Phone,
  Mail,
  FileText,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  BookOpen,
} from 'lucide-react';

const LegalRightsPage = () => {
  const [expandedSection, setExpandedSection] = useState('pdiRights');
  const [copiedEmail, setCopiedEmail] = useState(false);

  usePageMeta({
    title: 'Your Legal Rights as a Car Buyer in India (Consumer Protection Act)',
    description: 'Know your PDI rights under the Consumer Protection Act, 2019 - can you refuse delivery, escalation contacts, and a sample complaint email template for defective new cars.',
    path: '/legal',
  });

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(LEGAL_RIGHTS.sampleEscalationEmail.template);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const sections = [
    { id: 'pdiRights', icon: Shield, data: LEGAL_RIGHTS.pdiRights },
    { id: 'consumerProtection', icon: Scale, data: LEGAL_RIGHTS.consumerProtection },
    { id: 'dealerRefusal', icon: AlertTriangle, data: LEGAL_RIGHTS.dealerRefusal },
    { id: 'documentChecklist', icon: FileText, data: LEGAL_RIGHTS.documentChecklist },
    { id: 'warranty', icon: CheckCircle2, data: LEGAL_RIGHTS.warranty },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 animate-fade-in">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Scale className="w-8 h-8 text-brand-400" />
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-800">
          Know Your Rights
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Legal information for car buyers in India
        </p>
      </div>

      {/* Main Sections */}
      <div className="space-y-3 mb-6">
        {sections.map(({ id, icon: Icon, data }) => {
          const isExpanded = expandedSection === id;
          
          return (
            <div 
              key={id}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
            >
              <button
                onClick={() => setExpandedSection(isExpanded ? null : id)}
                className="w-full px-4 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <span className="font-semibold text-slate-800">{data.title}</span>
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>
              
              {isExpanded && (
                <div className="px-4 pb-4 animate-slide-down">
                  {data.content && (
                    <p className="text-sm text-slate-600 mb-4">{data.content}</p>
                  )}
                  
                  {data.points && (
                    <ul className="space-y-2">
                      {data.points.map((point, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {data.relevantSections && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-xl">
                      <p className="text-xs font-medium text-blue-800 mb-2">Relevant Sections:</p>
                      <div className="space-y-1">
                        {data.relevantSections.map((sec, i) => (
                          <div key={i} className="text-xs">
                            <span className="font-mono text-blue-700">{sec.section}</span>
                            <span className="text-blue-600"> - {sec.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {data.steps && (
                    <div className="space-y-3">
                      {data.steps.map((step) => (
                        <div key={step.step} className="flex gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">
                            {step.step}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{step.action}</p>
                            <p className="text-sm text-slate-600">{step.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {data.mandatory && (
                    <div className="space-y-2">
                      {data.mandatory.map((doc, i) => (
                        <div key={i} className="p-3 bg-slate-50 rounded-xl">
                          <p className="font-medium text-slate-800">{doc.document}</p>
                          <p className="text-xs text-brand-600 font-medium">{doc.importance}</p>
                          <p className="text-xs text-slate-500 mt-1">{doc.verify}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {data.warningActions && (
                    <div className="mt-4 p-3 bg-danger-50 border border-danger-200 rounded-xl">
                      <p className="text-xs font-medium text-danger-800 mb-2">Actions that may void warranty:</p>
                      <ul className="space-y-1">
                        {data.warningActions.map((action, i) => (
                          <li key={i} className="text-xs text-danger-700 flex items-center gap-1">
                            <span className="text-danger-400">•</span>
                            {action}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Escalation Contacts */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-6">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-4">
          <Phone className="w-5 h-5 text-slate-500" />
          {LEGAL_RIGHTS.escalationContacts.title}
        </h3>
        <div className="space-y-3">
          {LEGAL_RIGHTS.escalationContacts.contacts.map((contact, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
              <div>
                <p className="font-medium text-slate-800">{contact.name}</p>
                <p className="text-xs text-slate-500">{contact.type}</p>
              </div>
              {contact.number && (
                <a 
                  href={`tel:${contact.number}`}
                  className="px-3 py-1.5 bg-brand-500 text-white text-sm font-medium rounded-lg"
                >
                  {contact.number}
                </a>
              )}
              {contact.website && (
                <a 
                  href={`https://${contact.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 bg-blue-500 text-white text-sm font-medium rounded-lg flex items-center gap-1"
                >
                  Visit <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {contact.email && (
                <a 
                  href={`mailto:${contact.email}`}
                  className="px-3 py-1.5 bg-slate-600 text-white text-sm font-medium rounded-lg"
                >
                  Email
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Sample Escalation Email */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Mail className="w-5 h-5 text-slate-500" />
            {LEGAL_RIGHTS.sampleEscalationEmail.title}
          </h3>
          <button
            onClick={handleCopyEmail}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-200 transition-colors"
          >
            {copiedEmail ? (
              <>
                <Check className="w-4 h-4 text-brand-600" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy
              </>
            )}
          </button>
        </div>
        <div className="p-4">
          <p className="text-xs text-slate-500 mb-2">
            Subject: {LEGAL_RIGHTS.sampleEscalationEmail.subject}
          </p>
          <pre className="p-3 bg-slate-50 rounded-xl text-xs text-slate-700 whitespace-pre-wrap overflow-x-auto max-h-64 overflow-y-auto font-mono">
            {LEGAL_RIGHTS.sampleEscalationEmail.template}
          </pre>
          <p className="text-xs text-slate-400 mt-2 italic">
            Replace text in [brackets] with your actual information
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mt-6 p-4 bg-slate-100 rounded-xl">
        <div className="flex items-start gap-2">
          <BookOpen className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-slate-600">
            <strong>Disclaimer:</strong> This information is provided for general guidance only 
            and does not constitute legal advice. Laws and regulations may change. For specific 
            legal issues, please consult a qualified legal professional.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalRightsPage;
